<template>
  <div v-if="!loading && template">
    <h1 class="main-header">
      <translate
        translate-context="email_templates"
        :translate-params="{name: getEmailTypeLabel(template.email_type)}">
        Edit Email Template: %{name}
      </translate>
    </h1>

    <CCard>
      <CCardHeader>
        <div class="card-header-actions">
          <CButton
            variant="outline"
            color="success"
            @click="onReset"
            class="mr-2">
            <translate translate-context="form">Reset</translate>
          </CButton>
          <CButton color="primary" @click="onClickSave">
            <translate translate-context="form">Save</translate>
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <EmailTemplateForm
          ref="form"
          :data="template"
          @save="onSave"
          edit/>
      </CCardBody>
    </CCard>

    <!-- Debug Logs Section -->
    <DebugLogsSection :email-type="template.email_type"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import EmailTemplateForm from './components/EmailTemplateForm.vue'
import DebugLogsSection from './components/DebugLogsSection.vue'
import { RecordNotFound, SuccessNotification } from '@/domain/core/exception/exceptions'

export default {
  name: 'EditEmailTemplate',
  components: {
    EmailTemplateForm,
    DebugLogsSection
  },
  data() {
    return {
      loading: true
    }
  },
  computed: {
    ...mapGetters('emailTemplates', ['template', 'availableTypeByValue']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      }
      if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      if (this.scopeType === 'event-owner') {
        return this.$route.params.eventOwnerId
      }
      return null
    },

    listRouteName() {
      const names = {
        'global': 'admin.emailTemplates.list',
        'contract-manager': 'contractManager.emailTemplates.list',
        'event-owner': 'eventOwner.emailTemplates.list',
        'pica-service': 'event.emailTemplates.list'
      }
      return names[this.scopeType]
    }
  },
  async mounted() {
    try {
      // Set scope first
      await this.setScope({ type: this.scopeType, id: this.scopeId })

      // Then fetch the template
      await this.fetchEmailTemplate(this.$route.params.emailType)
    } catch (e) {
      if (e instanceof RecordNotFound) {
        await this.$router.push({ name: this.listRouteName })
        this.notifyError(this.$pgettext('email_templates', 'Template not found'))
      }
    }
    this.loading = false
  },
  methods: {
    ...mapActions('emailTemplates', ['setScope', 'fetchEmailTemplate', 'updateEmailTemplate']),

    getEmailTypeLabel(emailType) {
      const emailTypeConfig = this.availableTypeByValue(emailType)
      return emailTypeConfig ? emailTypeConfig.label : emailType
    },

    onClickSave() {
      this.$refs.form.onSave()
    },

    onReset() {
      this.$refs.form.onReset()
    },

    async onSave(data) {
      try {
        await this.updateEmailTemplate({
          emailType: this.$route.params.emailType,
          data: data
        })
        await this.$router.push({ name: this.listRouteName })
        throw new SuccessNotification(
          this.$pgettext('email_templates', 'Email template updated successfully!')
        )
      } catch (error) {
        if (error instanceof SuccessNotification) {
          throw error
        }
        // Handle validation errors
        if (error.response && error.response.status === 400) {
          const errors = error.response.data
          this.notifyError(this.$pgettext('email_templates', 'Please check the form for errors'))
        } else {
          this.notifyError(this.$pgettext('email_templates', 'Failed to update template'))
        }
      }
    }
  }
}
</script>

<style scoped>
.main-header {
  margin-bottom: 1.5rem;
}
</style>
